import { useState, useEffect, useCallback, useRef } from 'react';
import { Template, MediaType } from '../../core/template.schemas';
import { EntitySlotSelectionMap, EntityInstance, PostGenerationState, EntitySlotUtils, SelectorSelectionMap, AttributeOverrideMap } from '../types/PostGenerationTypes';
import { TemplateAnalysisService, TemplateAnalysisResult } from '../services/TemplateAnalysisService';
import { PostGenerationService, PostGenerationResult } from '../services/PostGenerationService';
import { useTemplateExport } from '../../hooks/useTemplateExport';

/**
 * Hook for managing post generation with slot-based entity selection
 */
export function usePostGeneration(template: Template | null) {
  // Core state
  const [analysisResult, setAnalysisResult] = useState<TemplateAnalysisResult | null>(null);
  const [entitySlots, setEntitySlots] = useState<EntitySlotSelectionMap>({});
  const [selectorSelections, setSelectorSelections] = useState<SelectorSelectionMap>({});
  const [manualOverrides, setManualOverrides] = useState<AttributeOverrideMap>({}); // New state for overrides
  const [generationState, setGenerationState] = useState<PostGenerationState>({
    currentStep: 'analysis',
    isGenerating: false
  });
  const [generatedPost, setGeneratedPost] = useState<PostGenerationResult | null>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  
  const { exportAsPNG } = useTemplateExport();
  
  // Ref to track previous template ID/update to prevent unnecessary resets on reference change
  const prevTemplateRef = useRef<{id: string, updatedAt?: string} | null>(null);

  // Cleanup generated Blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (generatedImageUrl && generatedImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(generatedImageUrl);
      }
    };
  }, [generatedImageUrl]);

  /**
   * Analyzes template for slot bindings when template changes
   */
  useEffect(() => {
    if (!template) {
      setAnalysisResult(null);
      setEntitySlots({});
      setSelectorSelections({});
      setGenerationState({ currentStep: 'analysis', isGenerating: false });
      setGeneratedPost(null);
      setGeneratedImageUrl(null);
      // Reset ref
      prevTemplateRef.current = null;
      return;
    }
    
    // Check if template effectively changed (ID or updatedAt)
    const currentUpdatedAt = template.meta?.updatedAt;
    const prev = prevTemplateRef.current;
    
    if (prev?.id === template.id && prev?.updatedAt === currentUpdatedAt) {
      // Template content hasn't changed, ignore reference change to prevent loop
      return;
    }
    
    // Update ref
    prevTemplateRef.current = { id: template.id, updatedAt: currentUpdatedAt };
    
    try {
      const result = TemplateAnalysisService.analyzeTemplate(template);
      setAnalysisResult(result);
      setEntitySlots(result.entitySlots);
      setSelectorSelections(result.selectors);
      
      const nextStep = (result.hasSlotBindings || result.hasSelectorBindings) ? 'selection' : 'preview';
      setGenerationState({ currentStep: nextStep, isGenerating: false });
      
    } catch (error) {
      console.error('Template analysis failed', error);
      setGenerationState({
        currentStep: 'analysis',
        isGenerating: false,
        error: error instanceof Error ? error.message : 'Analysis failed'
      });
    }
  }, [template]);

  /**
   * Helper to check for missing attributes in a slot
   */
  const getMissingAttributes = useCallback((slotKey: string, instance: EntityInstance | null, currentOverrides: AttributeOverrideMap): { attributePath: string; mediaType: MediaType }[] => {
    if (!instance || !analysisResult) return [];
    
    const slot = entitySlots[slotKey];
    if (!slot) return [];

    // Map attribute path to media type (prioritize first found media type if duplicates? check collisions?)
    const neededAttributes = new Map<string, string>();
    
    analysisResult.slotBindings.forEach(binding => {
      if (binding.slotId === slot.slotId) {
        neededAttributes.set(binding.attributePath, binding.mediaType);
      }
    });

    const missing: { attributePath: string; mediaType: MediaType }[] = [];

    neededAttributes.forEach((mediaType, path) => {
      // Check entity data
      const parts = path.split('.');
      let current = instance.data;
      let hasValue = true;
      
      for (const part of parts) {
        if (current === undefined || current === null) {
          hasValue = false;
          break;
        }
        current = current[part];
      }
      
      if (!hasValue || current === undefined || current === null || current === '') {
         missing.push({ attributePath: path, mediaType: mediaType as MediaType });
      }
    });

    return missing;
  }, [analysisResult, entitySlots]);

  /**
   * Updates entity selection for a specific slot and checks for missing attributes
   */
  const updateEntitySelection = useCallback((slotKey: string, entityInstance: EntityInstance | null) => {
    setEntitySlots(prev => {
        // Calculate missing attributes immediately
        const missing = getMissingAttributes(slotKey, entityInstance, manualOverrides);
        
        return {
            ...prev,
            [slotKey]: {
                ...prev[slotKey],
                selectedInstance: entityInstance,
                missingAttributes: missing
            }
        };
    });
  }, [manualOverrides, getMissingAttributes]);

  /**
   * Updates selector value for a given selectorId
   */
  const updateSelectorSelection = useCallback((selectorId: string, value: any) => {
    setSelectorSelections(prev => ({
      ...prev,
      [selectorId]: {
        ...prev[selectorId],
        selectedValue: value
      }
    }));
  }, []);

  /**
   * Checks if all required slots are selected
   */
  const areRequiredSlotsSelected = useCallback(() => {
    return EntitySlotUtils.areRequiredSlotsSelected(entitySlots);
  }, [entitySlots]);

  /**
   * Gets completion progress
   */
  const getProgress = useCallback(() => {
    return EntitySlotUtils.getCompletionProgress(entitySlots);
  }, [entitySlots]);

  /**
   * Validates current slot selection
   */
  const validateSelection = useCallback(() => {
    const baseValidation = EntitySlotUtils.validateSlotSelection(entitySlots);
    if (!baseValidation.isValid) return baseValidation;

    // Check for missing attributes that are not overridden
    const slotsWithMissing = Object.values(entitySlots).filter(
      slot => {
        if (!slot.selectedInstance || !slot.missingAttributes || slot.missingAttributes.length === 0) {
          return false;
        }
        
        const overrides = manualOverrides[slot.slotKey] || {};
        
        // Check if any missing attribute is NOT covered by an override
        return slot.missingAttributes.some(attr => {
          const val = overrides[attr.attributePath];
          return val === undefined || val === null || val === '';
        });
      }
    );

    if (slotsWithMissing.length > 0) {
      return {
        isValid: true,
        missingRequired: [],
        errors: []
      };
    }

    return baseValidation;
  }, [entitySlots, manualOverrides]);

  /**
   * Proceeds to next step in generation process
   */
  const proceedToNextStep = useCallback(() => {
    const { currentStep } = generationState;
    
    if (currentStep === 'selection') {
      const validation = validateSelection();
      if (!validation.isValid) {
        setGenerationState(prev => ({
          ...prev,
          error: validation.errors.join(', ')
        }));
        return;
      }
      setGenerationState(prev => ({ ...prev, currentStep: 'preview', error: undefined }));
    } else if (currentStep === 'preview') {
      generatePost();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationState, validateSelection]);

  /**
   * Goes back to previous step
   */
  const goToPreviousStep = useCallback(() => {
    const { currentStep } = generationState;
    
    if (currentStep === 'preview' || currentStep === 'generation') {
      setGenerationState(prev => ({ ...prev, currentStep: 'selection', error: undefined, isGenerating: false }));
      setGeneratedPost(null);
      setGeneratedImageUrl(null);
    }
  }, [generationState]);

  /**
   * Generates the final post with populated data
   */
  const generatePost = useCallback(async () => {
    if (!template || !analysisResult) {
      console.error('Cannot generate post: missing template or analysis result');
      return;
    }

    setGenerationState(prev => ({ 
      ...prev, 
      currentStep: 'generation', 
      isGenerating: true, 
      error: undefined 
    }));
    setGeneratedImageUrl(null);

    try {
      const result = PostGenerationService.generatePost(
        template,
        analysisResult.slotBindings,
        entitySlots,
        selectorSelections,
        manualOverrides
      );

      setGeneratedPost(result);
      
      if (result.success) {
        // Allow a small delay for the populated template to be ready in logic before exporting
        try {
          // Adaptive export settings for mobile devices to prevent crashes
          const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
          
          const imageUrl = await exportAsPNG(result.populatedTemplate, {
            scale: isMobile ? 1.0 : 2, // Reduce scale on mobile (1.0 is enough for 1080p and prevents re-compression)
            mimeType: isMobile ? 'image/jpeg' : 'image/png', // Use JPEG on mobile to reduce memory usage
            quality: isMobile ? 0.9 : 1,
            download: false,
            entitySlots,
            selectorSelections,
            manualOverrides,
          });
          setGeneratedImageUrl(imageUrl);

          setGenerationState(prev => ({ 
            ...prev, 
            isGenerating: false 
          }));
        } catch (exportError) {
          console.error('Export failed', exportError);
          setGenerationState(prev => ({ 
            ...prev, 
            isGenerating: false,
            error: 'Failed to generate image from template'
          }));
        }
      } else {
        console.error('Post generation failed', result.errors);
        setGenerationState(prev => ({ 
          ...prev, 
          isGenerating: false,
          error: result.errors.join(', ')
        }));
      }
    } catch (error) {
      console.error('Post generation error', error);
      setGenerationState(prev => ({ 
        ...prev, 
        isGenerating: false,
        error: error instanceof Error ? error.message : 'Generation failed'
      }));
    }
  }, [template, analysisResult, entitySlots, selectorSelections, exportAsPNG, manualOverrides]);

  /**
   * Resets the generation process
   */
  const reset = useCallback(() => {
    setEntitySlots(analysisResult?.entitySlots || {});
    setSelectorSelections(analysisResult?.selectors || {});
    setGeneratedPost(null);
    setGeneratedImageUrl(null);
    setGenerationState({
      currentStep: analysisResult?.hasSlotBindings ? 'selection' : 'preview',
      isGenerating: false
    });
  }, [analysisResult]);

  return {
    // State
    analysisResult,
    entitySlots,
    selectorSelections,
    generationState,
    generatedPost,
    generatedImageUrl,
    
    // Computed values
    hasSlotBindings: analysisResult?.hasSlotBindings || false,
    hasSelectorBindings: analysisResult?.hasSelectorBindings || false,
    requiredSlots: analysisResult?.requiredSlots || [],
    optionalSlots: analysisResult?.optionalSlots || [],
    progress: getProgress(),
    validation: validateSelection(),
    canProceed: areRequiredSlotsSelected(),
    
    // Actions
    updateEntitySelection,
    updateSelectorSelection,
    updateManualOverride: (slotKey: string, attributePath: string, value: any) => {
       setManualOverrides(prevOverrides => {
         const newOverrides = {
           ...prevOverrides,
           [slotKey]: {
             ...prevOverrides[slotKey],
             [attributePath]: value
           }
         };
         
         // Update entity slots to reflectresolved attributes
         setEntitySlots(prevSlots => {
           const slot = prevSlots[slotKey];
           if (!slot || !slot.selectedInstance) return prevSlots;
           
           const missing = getMissingAttributes(slotKey, slot.selectedInstance, newOverrides);
           
           if (JSON.stringify(missing) === JSON.stringify(slot.missingAttributes)) {
             return prevSlots;
           }

           return {
             ...prevSlots,
             [slotKey]: {
               ...slot,
               missingAttributes: missing
             }
           };
         });
         
         return newOverrides;
       });
    },
    proceedToNextStep,
    goToPreviousStep,
    generatePost,
    reset,
    manualOverrides, // Expose overrides
  };
}
